import * as React from "react";
import { AnalyticsDatePickerProps } from "../../types/stripe";

/**
 * AnalyticsDatePicker Component
 * 
 * A comprehensive date range picker specifically designed for analytics dashboards
 * with preset options and validation.
 */
const AnalyticsDatePicker: React.FC<AnalyticsDatePickerProps> = ({
  startDate,
  endDate,
  onDateChange,
  presets = [
    { label: 'Today', value: { start: new Date(), end: new Date() } },
    { 
      label: 'Yesterday', 
      value: { 
        start: new Date(Date.now() - 24 * 60 * 60 * 1000), 
        end: new Date(Date.now() - 24 * 60 * 60 * 1000) 
      } 
    },
    { 
      label: 'Last 7 days', 
      value: { 
        start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 
        end: new Date() 
      } 
    },
    { 
      label: 'Last 30 days', 
      value: { 
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 
        end: new Date() 
      } 
    },
    { 
      label: 'Last 90 days', 
      value: { 
        start: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), 
        end: new Date() 
      } 
    },
    { 
      label: 'This month', 
      value: { 
        start: new Date(new Date().getFullYear(), new Date().getMonth(), 1), 
        end: new Date() 
      } 
    },
    { 
      label: 'Last month', 
      value: { 
        start: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1), 
        end: new Date(new Date().getFullYear(), new Date().getMonth(), 0) 
      } 
    }
  ],
  maxDate,
  minDate,
  disabled = false,
  className = "",
  id
}) => {
  const [showPresets, setShowPresets] = React.useState(false);
  const [isCustomRange, setIsCustomRange] = React.useState(false);
  
  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const parseDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };

  const handlePresetSelect = (preset: typeof presets[0]) => {
    onDateChange(preset.value.start, preset.value.end);
    setIsCustomRange(false);
    setShowPresets(false);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = parseDate(e.target.value);
    onDateChange(newStartDate, endDate);
    setIsCustomRange(true);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = parseDate(e.target.value);
    onDateChange(startDate, newEndDate);
    setIsCustomRange(true);
  };

  const handleCustomRangeToggle = () => {
    setIsCustomRange(!isCustomRange);
    setShowPresets(false);
  };

  const getCurrentPresetLabel = (): string => {
    if (isCustomRange || !startDate || !endDate) return 'Custom Range';
    
    const preset = presets.find(p => {
      const presetStart = p.value.start.toDateString();
      const presetEnd = p.value.end.toDateString();
      const currentStart = startDate.toDateString();
      const currentEnd = endDate.toDateString();
      return presetStart === currentStart && presetEnd === currentEnd;
    });
    
    return preset?.label || 'Custom Range';
  };

  const isValidRange = startDate && endDate && startDate <= endDate;
  const hasDateRange = startDate && endDate;

  return (
    <div className={`analytics-date-picker ${className}`.trim()} id={id}>
      <div className="date-picker-container">
        {/* Preset Dropdown */}
        <div className="dropdown me-2">
          <button
            className="btn btn-outline-secondary dropdown-toggle d-flex align-items-center"
            type="button"
            data-bs-toggle="dropdown"
            disabled={disabled}
            onClick={() => setShowPresets(!showPresets)}
          >
            <i className="fas fa-calendar-alt me-2" aria-hidden="true"></i>
            {getCurrentPresetLabel()}
          </button>
          {showPresets && (
            <div className="dropdown-menu show">
              {presets.map((preset, index) => (
                <button
                  key={index}
                  className="dropdown-item"
                  type="button"
                  onClick={() => handlePresetSelect(preset)}
                >
                  <i className="fas fa-clock me-2 text-muted" aria-hidden="true"></i>
                  {preset.label}
                </button>
              ))}
              <hr className="dropdown-divider" />
              <button
                className="dropdown-item"
                type="button"
                onClick={handleCustomRangeToggle}
              >
                <i className="fas fa-cog me-2 text-muted" aria-hidden="true"></i>
                Custom Range
              </button>
            </div>
          )}
        </div>

        {/* Custom Date Range Inputs */}
        {(isCustomRange || getCurrentPresetLabel() === 'Custom Range') && (
          <div className="d-flex align-items-center gap-2">
            <div className="form-group">
              <label htmlFor={`${id}-start-date`} className="visually-hidden">Start Date</label>
              <input
                id={`${id}-start-date`}
                type="date"
                className={`form-control ${!isValidRange && hasDateRange ? 'is-invalid' : ''}`}
                value={formatDate(startDate)}
                onChange={handleStartDateChange}
                max={formatDate(maxDate || new Date())}
                min={formatDate(minDate)}
                disabled={disabled}
                placeholder="Start date"
              />
              {!isValidRange && hasDateRange && (
                <div className="invalid-feedback">
                  Start date must be before end date
                </div>
              )}
            </div>

            <span className="text-muted">to</span>

            <div className="form-group">
              <label htmlFor={`${id}-end-date`} className="visually-hidden">End Date</label>
              <input
                id={`${id}-end-date`}
                type="date"
                className={`form-control ${!isValidRange && hasDateRange ? 'is-invalid' : ''}`}
                value={formatDate(endDate)}
                onChange={handleEndDateChange}
                max={formatDate(maxDate || new Date())}
                min={formatDate(minDate)}
                disabled={disabled}
                placeholder="End date"
              />
            </div>
          </div>
        )}

        {/* Date Range Summary */}
        {isValidRange && (
          <div className="date-range-summary ms-3 d-flex align-items-center">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1" aria-hidden="true"></i>
              {startDate && endDate && (
                <>
                  {Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1} day{Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) !== 0 ? 's' : ''}
                  {startDate.toDateString() !== endDate.toDateString() && (
                    <> ({startDate.toLocaleDateString()} - {endDate.toLocaleDateString()})</>
                  )}
                </>
              )}
            </small>
          </div>
        )}

        {/* Clear Button */}
        {hasDateRange && !disabled && (
          <button
            type="button"
            className="btn btn-link text-muted p-1 ms-2"
            onClick={() => onDateChange(null, null)}
            title="Clear dates"
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        )}
      </div>

      <style>{`
        .analytics-date-picker {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .date-picker-container {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .form-group {
          margin-bottom: 0;
        }

        .dropdown-menu {
          max-height: 300px;
          overflow-y: auto;
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          padding: 0.5rem 1rem;
        }

        .dropdown-item:hover {
          background-color: var(--bs-gray-100);
        }

        .date-range-summary {
          white-space: nowrap;
        }

        @media (max-width: 768px) {
          .analytics-date-picker {
            flex-direction: column;
            align-items: stretch;
          }

          .date-picker-container {
            flex-direction: column;
            align-items: stretch;
          }

          .dropdown {
            width: 100%;
          }

          .dropdown .btn {
            width: 100%;
            justify-content: space-between;
          }
        }

        /* Accessibility improvements */
        .dropdown-toggle:focus {
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }

        .form-control:focus {
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }

        /* Animation for dropdown */
        .dropdown-menu {
          animation: fadeIn 0.15s ease-in-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

AnalyticsDatePicker.displayName = 'AnalyticsDatePicker';

export default AnalyticsDatePicker;